import React, { useState } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Chip,
    Grid,
    Divider,
    CircularProgress,
    useTheme,
    Card,
    CardContent,
} from '@material-ui/core'
import {
    Mail,
    Phone,
    Link as LinkIcon,
    Search,
    Flag as CampaignIcon,
} from '@material-ui/icons'
import FindReplaceIcon from '@material-ui/icons/FindReplace'
import { makeStyles, Theme } from '@material-ui/core/styles'

import {
    Prospect,
    DetailedCampaignProspect,
} from '../../../../models/Outreach/types'
import {
    calculateEnrichmentScore,
    getPipelineStage,
    getStatusColor,
} from '../utils'
import { CampaignEmailUnsubscribeIndicator } from '../../CampaignEmailUnsubscribeIndicator'

interface Props {
    open: boolean
    prospect: Prospect | null
    campaignProspect?: DetailedCampaignProspect | null
    onClose: () => void
    apartmentId: number | undefined
}

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            maxWidth: '900px',
            width: '100%',
        },
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    dialogContent: {
        padding: theme.spacing(3),
    },
    section: {
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        fontSize: '1.125rem',
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        gap: theme.spacing(2),
    },
    fieldLabel: {
        minWidth: '120px',
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    fieldValue: {
        flex: 1,
        fontSize: '0.875rem',
    },
    contactInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    enrichmentButton: {
        padding: theme.spacing(0.5),
    },
    chip: {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
    },
    engagementCard: {
        marginBottom: theme.spacing(2),
    },
    engagementMetric: {
        textAlign: 'center',
    },
    engagementValue: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    engagementLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    editButton: {
        marginLeft: theme.spacing(1),
    },
    editableField: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
}))

export const ProspectDetailDialog: React.FC<Props> = ({
    open,
    prospect,
    campaignProspect,
    onClose,
    apartmentId,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const [isEditing, setIsEditing] = useState(false)
    const [editedName, setEditedName] = useState('')

    // Note: Enrichment requires a listId, which we don't have in the leads context
    // For now, we'll disable enrichment from this dialog
    // In the future, we could add a prospect-to-list mapping or a direct enrichment endpoint
    const [enrichingProspects, setEnrichingProspects] = useState<
        Array<{
            id: number
            field: string
        }>
    >([])

    React.useEffect(() => {
        if (prospect) {
            setEditedName(prospect.name)
        }
    }, [prospect])

    if (!prospect) return null

    const enrichmentScore = calculateEnrichmentScore(prospect)
    const pipelineStage = campaignProspect
        ? getPipelineStage(campaignProspect)
        : 'prospect'
    const statusColor = getStatusColor(pipelineStage)

    const handleEnrichField = (
        field: 'email' | 'phone' | 'linkedinProfile',
    ) => {
        // TODO: Implement enrichment when we have a way to get listId or use direct endpoint
        // For now, show a message that enrichment should be done from the prospect list detail page
        console.warn(
            'Enrichment from leads page not yet supported. Please use prospect list detail page.',
        )
    }

    const handleSave = () => {
        // TODO: Implement save mutation
        setIsEditing(false)
    }

    const handleCancel = () => {
        setEditedName(prospect.name)
        setIsEditing(false)
    }

    const isEnriching = (field: 'email' | 'phone' | 'linkedinProfile') => {
        return enrichingProspects.some(
            (p) => p.id === prospect.id && p.field === field,
        )
    }

    const renderEnrichmentField = (
        label: string,
        field: 'email' | 'phone' | 'linkedinProfile',
        value: string,
        icon: React.ReactNode,
        attempted: boolean,
    ) => {
        const hasValue = !!value
        const enriching = isEnriching(field)
        const color = hasValue ? '#10b981' : attempted ? '#ef4444' : '#9ca3af'

        return (
            <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>{label}</Typography>
                <Box
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <IconButton
                        size="small"
                        className={classes.enrichmentButton}
                        onClick={() => handleEnrichField(field)}
                        disabled={enriching}
                    >
                        {enriching ? (
                            <CircularProgress
                                size={16}
                                style={{ color: '#3b82f6' }}
                            />
                        ) : attempted ? (
                            <FindReplaceIcon style={{ fontSize: 16, color }} />
                        ) : (
                            <Search style={{ fontSize: 16, color }} />
                        )}
                    </IconButton>
                    {enriching ? (
                        <Typography
                            variant="body2"
                            style={{ color: '#9ca3af', fontStyle: 'italic' }}
                        >
                            Enriching...
                        </Typography>
                    ) : hasValue ? (
                        <Box className={classes.contactInfo}>
                            {icon}
                            <Typography variant="body2">{value}</Typography>
                        </Box>
                    ) : (
                        <Typography
                            variant="body2"
                            style={{
                                color,
                                fontStyle: 'italic',
                            }}
                        >
                            {attempted ? 'Not found' : 'Not enriched'}
                        </Typography>
                    )}
                </Box>
            </Box>
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.dialog}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle className={classes.dialogTitle}>
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(2),
                    }}
                >
                    <Typography variant="h6">Prospect Details</Typography>
                    <Chip
                        label={
                            pipelineStage.charAt(0).toUpperCase() +
                            pipelineStage.slice(1)
                        }
                        size="small"
                        style={{
                            backgroundColor: statusColor,
                            color: 'white',
                        }}
                    />
                </Box>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {/* Basic Info */}
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Basic Information
                    </Typography>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.fieldLabel}>
                            Name
                        </Typography>
                        <Box className={classes.editableField}>
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="flex-start"
                                style={{ gap: 8 }}
                            >
                                <Typography className={classes.fieldValue}>
                                    {prospect.name}
                                </Typography>
                                <CampaignEmailUnsubscribeIndicator
                                    prospect={prospect}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Divider style={{ margin: theme.spacing(3, 0) }} />

                {/* Contact Information */}
                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        Contact Information
                    </Typography>
                    {renderEnrichmentField(
                        'Email',
                        'email',
                        prospect.email.value,
                        <Mail
                            style={{ height: 16, width: 16, color: '#10b981' }}
                        />,
                        !!prospect.email.last_attempted,
                    )}
                    {renderEnrichmentField(
                        'Phone',
                        'phone',
                        prospect.phone.value,
                        <Phone
                            style={{ height: 16, width: 16, color: '#f59e0b' }}
                        />,
                        !!prospect.phone.last_attempted,
                    )}
                    {renderEnrichmentField(
                        'LinkedIn',
                        'linkedinProfile',
                        prospect.linkedinProfile.value,
                        <LinkIcon
                            style={{ height: 16, width: 16, color: '#8b5cf6' }}
                        />,
                        !!prospect.linkedinProfile.last_attempted,
                    )}
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.fieldLabel}>
                            Enrichment Score
                        </Typography>
                        <Box
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <Box
                                style={{
                                    flex: 1,
                                    height: 8,
                                    backgroundColor: theme.palette.grey[200],
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    style={{
                                        width: `${enrichmentScore.score}%`,
                                        height: '100%',
                                        backgroundColor: statusColor,
                                        transition: 'width 0.3s ease',
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="body2"
                                style={{ minWidth: 50, textAlign: 'right' }}
                            >
                                {enrichmentScore.score}%
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider style={{ margin: theme.spacing(3, 0) }} />

                {/* Campaign Information */}
                {campaignProspect && (
                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Campaign Information
                        </Typography>
                        <Box className={classes.fieldRow}>
                            <Typography className={classes.fieldLabel}>
                                Campaign
                            </Typography>
                            <Box style={{ flex: 1 }}>
                                <Chip
                                    icon={
                                        <CampaignIcon
                                            style={{ fontSize: 16 }}
                                        />
                                    }
                                    label={campaignProspect.campaign.name}
                                    className={classes.chip}
                                    style={{
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                    }}
                                />
                            </Box>
                        </Box>
                        <Box className={classes.fieldRow}>
                            <Typography className={classes.fieldLabel}>
                                Status
                            </Typography>
                            <Box style={{ flex: 1 }}>
                                <Chip
                                    label={
                                        campaignProspect.status_display ||
                                        campaignProspect.status
                                    }
                                    size="small"
                                    style={{
                                        backgroundColor: statusColor,
                                        color: 'white',
                                    }}
                                />
                            </Box>
                        </Box>
                        {campaignProspect.current_step && (
                            <Box className={classes.fieldRow}>
                                <Typography className={classes.fieldLabel}>
                                    Current Step
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    Step{' '}
                                    {campaignProspect.current_step.step_order}:{' '}
                                    {campaignProspect.current_step.step_type}
                                </Typography>
                            </Box>
                        )}
                        {campaignProspect.last_step_sent_at && (
                            <Box className={classes.fieldRow}>
                                <Typography className={classes.fieldLabel}>
                                    Last Step Sent
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {new Date(
                                        campaignProspect.last_step_sent_at,
                                    ).toLocaleString()}
                                </Typography>
                            </Box>
                        )}
                        {campaignProspect.next_step_scheduled_at && (
                            <Box className={classes.fieldRow}>
                                <Typography className={classes.fieldLabel}>
                                    Next Step Scheduled
                                </Typography>
                                <Typography className={classes.fieldValue}>
                                    {new Date(
                                        campaignProspect.next_step_scheduled_at,
                                    ).toLocaleString()}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Engagement Metrics */}
                {campaignProspect && (
                    <>
                        <Divider style={{ margin: theme.spacing(3, 0) }} />
                        <Box className={classes.section}>
                            <Typography className={classes.sectionTitle}>
                                Engagement Metrics
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Card className={classes.engagementCard}>
                                        <CardContent
                                            className={classes.engagementMetric}
                                        >
                                            <Typography
                                                className={
                                                    classes.engagementValue
                                                }
                                            >
                                                {campaignProspect.messages_sent}
                                            </Typography>
                                            <Typography
                                                className={
                                                    classes.engagementLabel
                                                }
                                            >
                                                Messages Sent
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={4}>
                                    <Card className={classes.engagementCard}>
                                        <CardContent
                                            className={classes.engagementMetric}
                                        >
                                            <Typography
                                                className={
                                                    classes.engagementValue
                                                }
                                            >
                                                {
                                                    campaignProspect.messages_opened
                                                }
                                            </Typography>
                                            <Typography
                                                className={
                                                    classes.engagementLabel
                                                }
                                            >
                                                Messages Opened
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={4}>
                                    <Card className={classes.engagementCard}>
                                        <CardContent
                                            className={classes.engagementMetric}
                                        >
                                            <Typography
                                                className={
                                                    classes.engagementValue
                                                }
                                            >
                                                {
                                                    campaignProspect.messages_clicked
                                                }
                                            </Typography>
                                            <Typography
                                                className={
                                                    classes.engagementLabel
                                                }
                                            >
                                                Messages Clicked
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                )}

                {/* Custom Data */}
                {prospect.custom_data &&
                    Object.keys(prospect.custom_data).length > 0 && (
                        <>
                            <Divider style={{ margin: theme.spacing(3, 0) }} />
                            <Box className={classes.section}>
                                <Typography className={classes.sectionTitle}>
                                    Custom Data
                                </Typography>
                                {Object.entries(prospect.custom_data).map(
                                    ([key, value]) => (
                                        <Box
                                            key={key}
                                            className={classes.fieldRow}
                                        >
                                            <Typography
                                                className={classes.fieldLabel}
                                            >
                                                {key
                                                    .replace(/_/g, ' ')
                                                    .replace(/\b\w/g, (l) =>
                                                        l.toUpperCase(),
                                                    )}
                                            </Typography>
                                            <Typography
                                                className={classes.fieldValue}
                                            >
                                                {value ? String(value) : '—'}
                                            </Typography>
                                        </Box>
                                    ),
                                )}
                            </Box>
                        </>
                    )}
            </DialogContent>

            <DialogActions
                style={{
                    padding: theme.spacing(2, 3),
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
